import {
  ImageFile,
  isOrgLayout,
  OrderDirection,
  PaginationInfo,
  RestrictDuplicateUrlOfCardsInTimeFrame,
} from '../shared';
import { graphqlApiWithPersisted } from './graphqlApi';
import { OrganisationUserOrderBy } from './organisation';
import { User, UserGender, UserInvitedBy, UserStatus } from './user';

export enum ChannelAppTabAction {
  CHATS = 'CHATS',
  NEWS_FEED = 'NEWS_FEED',
  PINNED_STORY = 'PINNED_STORY',
  PINNED_STORY_2 = 'PINNED_STORY_2',
  PINNED_STORY_3 = 'PINNED_STORY_3',
  PINNED_STORY_4 = 'PINNED_STORY_4',
  PINNED_STORY_5 = 'PINNED_STORY_5',
  PROFILE = 'PROFILE',
  STORIES = 'STORIES',
  STORIES_2 = 'STORIES_2',
  STORIES_3 = 'STORIES_3',
  STORIES_4 = 'STORIES_4',
  STORIES_5 = 'STORIES_5',
  USER_DIRECTORY = 'USER_DIRECTORY',
  BOOKMARKS = 'BOOKMARKS',
  EVENTS = 'EVENTS',
}

export interface ChannelPreview {
  id: number;
  name: string;
  subdomain: string;
}

export interface ChannelPermissions {
  channelInAppMessageAllowToCreate: boolean;
  channelPushNotificationAllowToCreate: boolean;
  chatGroupAllowToCreate: boolean;
  storyAllowToCreate: boolean;
  eventAllowToCreate: boolean;
}

interface ChannelAppTabTitle {
  language: string;
  localization: string;
}

interface ChannelAppTab {
  id: number;
  action: { type: ChannelAppTabAction; storyId?: number };
  iconUrl: string;
  title: ChannelAppTabTitle[];
}

export interface StoryPreview {
  id: number;
  subtitle: string;
  title: string;
}

interface ChannelAppTabIconsSettings {
  x1: string;
  x3: string;
}

interface ChannelAppTabSettings {
  id: number;
  action: { type: ChannelAppTabAction };
  defaultIconUrl: ChannelAppTabIconsSettings;
  iconUrl: ChannelAppTabIconsSettings;
  title: ChannelAppTabTitle[];
}

export interface ChannelAppTabSetting {
  appTabs: ChannelAppTabSettings[];
  initialAppTabId: number;
}

export interface Channel {
  id: number;
  name: string;
  position: number;
  allAppTabs: ChannelAppTab[];
  appTabs: ChannelAppTab[];
  appTabsSettings: ChannelAppTabSetting;
  image: ImageFile | null;
  dashboardUrl: string;
  locale: string;
  localeId: number;
  role: { id: number };
  subdomain: string;
  userNotificationPreference: { allowToReceiveChannelNotification: boolean };
  restrictDuplicateUrlOfCards: RestrictDuplicateUrlOfCardsInTimeFrame;
  permissions: ChannelPermissions;
  isArchived: boolean;
  readersCount: number;
  curatorsCount: number;
  createdAt: string;
  accessedAt: string;
  pinnedStories: StoryPreview[];
  pinnedStoryId?: number;
  pinnedStoryId2?: number;
  pinnedStoryId3?: number;
  pinnedStoryId4?: number;
  pinnedStoryId5?: number;
}

export interface ChannelsArgs {
  filters: {
    excludeArchived?: boolean;
    onlyDefault?: boolean;
  };
}

interface ChannelsResponse {
  data: {
    channels: Channel[];
  };
}

export interface ChannelNotificationPreferenceUpdateArgs {
  channelId: number;
  fields: { allowToReceiveChannelNotification: boolean };
}

interface ChannelNotificationPreferenceUpdateResponse {
  data: {
    channelUserNotificationPreferenceUpdate: {
      payload: boolean;
    };
  };
}

export interface ChannelUsersFilters {
  query?: string;
  genders?: UserGender[];
  filterDepartments?: string[];
  filterPositions?: string[];
  filterSubscriptions?: string[];
  organisationTagsId?: number[];
  usersId?: number[];
  rolesId?: number[];
  queryLocation?: string;
  excludeBanned?: boolean;
  onboardingMethods?: [UserInvitedBy | null];
  statuses?: UserStatus[];
}

export interface ChannelUsersArgs {
  channelId: number;
  page?: number;
  size?: number;
  orderBy?: OrganisationUserOrderBy;
  orderDirection?: OrderDirection;
  filters?: ChannelUsersFilters;
  fetchManagementFields?: boolean;
}

interface ChannelUsersPayload {
  items: User[];
  pageInfo: PaginationInfo;
}

interface ChannelUsersResponse {
  data: {
    channelUsers: ChannelUsersPayload;
  };
}

interface ChannelBySubdomainArgs {
  subdomain: string;
}

export interface ChannelAppLocalizedTitleTab {
  language: string;
  localization: string;
}

export interface AppTab {
  action: { type: ChannelAppTabAction; storyId?: number };
  iconUrl: string;
  title: ChannelAppLocalizedTitleTab[];
}

interface ChannelBySubdomainResponse {
  data: {
    channelBySubdomain: {
      payload: Channel;
    };
  };
}

export interface ChannelCreateArgs {
  fields: {
    name: string;
    subdomain: string;
    imageId?: number;
  };
}

interface ChannelCreateData {
  payload: Channel;
  error: { __typename: string; message: string };
}

interface ChannelCreateResponse {
  data: {
    channelCreate: ChannelCreateData;
  };
}

export interface ChannelAppTabUpdateInput {
  action: { type: ChannelAppTabAction };
  iconUrl: ChannelAppTabIconsSettings;
  title: ChannelAppLocalizedTitleTab[];
}

export interface ChannelUpdateArgs {
  channelId: number;
  fields: {
    name?: string;
    subdomain?: string;
    imageId?: number | null;
    isArchived?: boolean;
    localeId?: number;
    restrictDuplicateUrlOfCards?: RestrictDuplicateUrlOfCardsInTimeFrame;
    appTabs?: ChannelAppTabUpdateInput[];
  };
}

interface ChannelUpdateData {
  payload: Channel;
  error: { __typename: string; message: string };
}

interface ChannelUpdateResponse {
  data: {
    channelUpdate: ChannelUpdateData;
  };
}

interface ChannelUpdatePositionArgs {
  channelId: number;
  position: number;
}

interface ChannelUpdatePositionResponse {
  data: {
    channelUpdatePosition: {
      payload: boolean;
    };
  };
}

export interface ChannelDeleteArgs {
  channelId: number;
}

interface ChannelDeleteData {
  payload: boolean;
  error: { __typename: string; message: string };
}

interface ChannelDeleteResponse {
  data: {
    channelDelete: ChannelDeleteData;
  };
}

export const channelsNewApi = graphqlApiWithPersisted.injectEndpoints({
  endpoints: (builder) => ({
    channels: builder.query<Channel[], ChannelsArgs>({
      query: ({ filters }) => ({
        url: '/graphql/webapp?channels',
        method: 'POST',
        body: {
          query: `query Channels($filters: GetChannelsFiltersArgsType) {
            channels(filters: $filters) {
              id
              name
              position
              subdomain
              pinnedStoryId
              pinnedStoryId2
              userNotificationPreference {
                allowToReceiveChannelNotification
              }
              permissions {
                channelInAppMessageAllowToCreate
                channelPushNotificationAllowToCreate
                chatGroupAllowToCreate
                storyAllowToCreate
                eventAllowToCreate
              }
              image {
                url
              }
              ${
                isOrgLayout()
                  ? `pinnedStories { id } isArchived readersCount curatorsCount createdAt accessedAt`
                  : ''
              }
            }
          }`,
          variables: { filters },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: ChannelsResponse) => response.data.channels,
    }),
    channelNotificationPreferenceUpdate: builder.mutation<
      boolean,
      ChannelNotificationPreferenceUpdateArgs
    >({
      query: ({ channelId, fields }) => ({
        url: '/graphql/webapp?channelUserNotificationPreferenceUpdate',
        method: 'POST',
        body: {
          query: `mutation ChannelUserNotificationPreferenceUpdate($input: ChannelUserNotificationPreferenceUpdateInput!) {
            channelUserNotificationPreferenceUpdate(input: $input) {
              payload
              error {
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: {
            input: { channelId, fields },
          },
        },
      }),
      transformResponse: (response: ChannelNotificationPreferenceUpdateResponse) =>
        response.data.channelUserNotificationPreferenceUpdate.payload,
    }),
    channelUsers: builder.query<ChannelUsersPayload, ChannelUsersArgs>({
      query: ({
        channelId,
        page = 1,
        size = 15,
        orderBy = OrganisationUserOrderBy.SCREEN_NAME,
        orderDirection = OrderDirection.ASC,
        filters,
        fetchManagementFields,
      }) => ({
        url: '/graphql/webapp?channelUsers',
        method: 'POST',
        body: {
          query: `query ChannelUsers($channelId: Int!, $size: Int, $page: Int, $orderBy: OrganisationUserOrderByFieldsEnum!, $orderDirection: OrderDirectionEnum!, $filters: GetChannelUsersFiltersArgsType) {
            channelUsers(channelId: $channelId, size: $size, page: $page, orderBy: $orderBy, orderDirection: $orderDirection, filters: $filters) {
              items {
                id
                avatar {
                  averageColor
                  externalId
                  externalType
                  id
                  originalFilename
                  rightholder
                  status
                  statusCopyrightId
                  thumb
                  url
                }
                department
                email
                phone
                position
                screenName
                roleId
                gender
                location {
                  text
                }
                ${
                  fetchManagementFields
                    ? `
                    accessedChannels {
                      id, name, role, subdomain
                    }
                    authorizedAt
                    registeredAt
                    isBanned
                    organisationTags {
                      id
                      name
                    }
                    statistic {
                      comments
                      likesOnComments
                      posts
                      reactions
                      shares
                    }
                    companyId
                    subscription
                    referralUsersCount
                    referredByUser {
                      screenName
                    }
                    onboardingMethod`
                    : ''
                }
              }
              pageInfo {
                page
                perPage
                hasNextPage
                hasPreviousPage
                totalItems
                totalPages
              }
            }
          }`,
          variables: { channelId, page, size, orderBy, orderDirection, filters },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: ChannelUsersResponse) => response.data.channelUsers,
    }),
    channelBySubdomain: builder.query<Channel, ChannelBySubdomainArgs>({
      query: ({ subdomain }) => ({
        url: '/graphql/webapp?channelBySubdomain',
        method: 'POST',
        body: {
          query: `query ChannelBySubdomain($subdomain: String!) {
            channelBySubdomain(subdomain: $subdomain) {
              payload {
                id
                name
                subdomain
                dashboardUrl
                localeId
                restrictDuplicateUrlOfCards
                pinnedStoryId
                pinnedStoryId2
                pinnedStoryId3
                pinnedStoryId4
                pinnedStoryId5
                role {
                  id
                }
                image {
                  id
                  url
                }
                appTabs {
                  iconUrl
                  action {
                    type
                    ... on ChannelAppTabPinnedStory {
                      storyId
                    }
                  }
                  title {
                    language
                    localization
                  }
                }
                allAppTabs {
                  iconUrl
                  action {
                    type
                    ... on ChannelAppTabPinnedStory {
                      storyId
                    }
                  }
                  title {
                    language
                    localization
                  }
                }
                appTabsSettings {
                  appTabs {
                    id
                    action {
                      type
                    }
                    defaultIconUrl {
                      x1
                      x3
                    }
                    iconUrl {
                      x1
                      x3
                    }
                    title {
                      language
                      localization
                    }
                  }
                  initialAppTabId
                }
                permissions {
                  channelInAppMessageAllowToCreate
                  channelPushNotificationAllowToCreate
                  chatGroupAllowToCreate
                  storyAllowToCreate
                  eventAllowToCreate
                }
              }
            }
          }`,
          variables: { subdomain },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: ChannelBySubdomainResponse) =>
        response.data.channelBySubdomain.payload,
    }),
    channelCreate: builder.mutation<ChannelCreateData, ChannelCreateArgs>({
      query: ({ fields }) => ({
        url: '/graphql/webapp?channelCreate',
        method: 'POST',
        body: {
          query: `mutation ChannelCreate($input: ChannelCreateInput!) {
            channelCreate(input: $input) {
              payload {
                id
                name
                subdomain
                position
                isArchived
                readersCount
                curatorsCount
                createdAt
                accessedAt
                pinnedStories {
                  id
                }
              }
              error {
                __typename
                ... on ChannelDuplicateSubdomainError {
                  message
                }
                ... on ChannelSubdomainMalformedError {
                  message
                }
                ... on ChannelValidationError {
                  message
                }
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: { input: { fields } },
        },
      }),
      transformResponse: (response: ChannelCreateResponse) => response.data.channelCreate,
    }),
    channelUpdate: builder.mutation<ChannelUpdateData, ChannelUpdateArgs>({
      query: ({ channelId, fields }) => ({
        url: '/graphql/webapp?channelUpdate',
        method: 'POST',
        body: {
          query: `mutation ChannelUpdate($input: ChannelUpdateInput!) {
            channelUpdate(input: $input) {
              payload {
                id
                name
                position
                isArchived
                readersCount
                curatorsCount
                createdAt
                accessedAt
                pinnedStories {
                  id
                }
                image {
                  url
                }
                appTabs {
                  iconUrl
                  action {
                    type
                    ... on ChannelAppTabPinnedStory {
                      storyId
                    }
                  }
                  title {
                    language
                    localization
                  }
                }
                allAppTabs {
                  iconUrl
                  action {
                    type
                    ... on ChannelAppTabPinnedStory {
                      storyId
                    }
                  }
                  title {
                    language
                    localization
                  }
                }
                appTabsSettings {
                  appTabs {
                    id
                    action {
                      type
                    }
                    defaultIconUrl {
                      x1
                      x3
                    }
                    iconUrl {
                      x1
                      x3
                    }
                    title {
                      language
                      localization
                    }
                  }
                  initialAppTabId
                }
              }
              error {
                __typename
                ... on ChannelDuplicateSubdomainError {
                  message
                }
                ... on ChannelSubdomainMalformedError {
                  message
                }
                ... on ChannelValidationError {
                  message
                }
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: { input: { channelId, fields } },
        },
      }),
      transformResponse: (response: ChannelUpdateResponse) => response.data.channelUpdate,
    }),
    channelUpdatePosition: builder.mutation<boolean, ChannelUpdatePositionArgs>({
      query: ({ channelId, position }) => ({
        url: '/graphql/webapp?channelUpdatePosition',
        method: 'POST',
        body: {
          query: `mutation ChannelUpdatePosition($input: ChannelUpdatePositionInput!) {
            channelUpdatePosition(input: $input) {
              payload
              error {
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: {
            input: { channelId, position },
          },
        },
      }),
      transformResponse: (response: ChannelUpdatePositionResponse) =>
        response.data.channelUpdatePosition.payload,
    }),
    channelDelete: builder.mutation<boolean, ChannelDeleteArgs>({
      query: ({ channelId }) => ({
        url: '/graphql/webapp?channelDelete',
        method: 'POST',
        body: {
          query: `mutation ChannelDelete($input: ChannelDeleteInput!) {
            channelDelete(input: $input) {
              payload
              error {
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: { input: { channelId } },
        },
      }),
      transformResponse: (response: ChannelDeleteResponse) => response.data.channelDelete.payload,
    }),
  }),
});
